// components/CalendarView.tsx

import React from "react";
import { CalendarView as ViewType, Appointment } from "../interfaces/types";

interface CalendarViewProps {
  currentView: ViewType;
  selectedDate: Date;
  currentMonth: Date;
  appointments: Appointment[];
  onViewChange: (view: ViewType) => void;
  onDateSelect: (date: Date) => void;
  onMonthChange: (month: Date) => void;
}

// =================================================================================
// 1. Helper Functions for Week View
// =================================================================================

// Get the dates for the current week starting on Monday
const getDatesOfWeek = (date: Date): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date(date);

  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = currentDate.getDay();

  // Calculate the difference to get to Monday (if Sunday is 0, diff is 6)
  // (dayOfWeek + 6) % 7 calculates days to subtract to reach Monday (1)
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Set the date to the start of the week (Monday)
  currentDate.setDate(currentDate.getDate() - daysToSubtract);

  for (let i = 0; i < 7; i++) {
    const d = new Date(currentDate);
    d.setDate(currentDate.getDate() + i);
    dates.push(d);
  }
  return dates;
};

// =================================================================================
// CalendarView Component
// =================================================================================
const CalendarView: React.FC<CalendarViewProps> = ({
  currentView,
  selectedDate,
  currentMonth,
  appointments,
  onViewChange,
  onDateSelect,
  onMonthChange,
}) => {
  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const dayNames = [
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
    "Domingo",
  ];

  // ¡ELIMINADO! La constante timeSlots ya no es necesaria aquí.
  /*
  const timeSlots = [
     "09:00 AM", ...
  ];
  */

  const handlePrevMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    onMonthChange(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    onMonthChange(newMonth);
  };

  const getViewTitle = () => {
    const month = monthNames[currentMonth.getMonth()];
    const year = currentMonth.getFullYear();

    switch (currentView) {
      case "day":
        const dayName =
          dayNames[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1];
        return `${dayName}, ${selectedDate.getDate()} de ${month}`;
      case "week":
        const weekDates = getDatesOfWeek(selectedDate);
        const weekStart = weekDates[0];
        const weekEnd = weekDates[6];

        const startMonth = monthNames[weekStart.getMonth()];
        const endMonth = monthNames[weekEnd.getMonth()];
        const endYear = weekEnd.getFullYear();

        // Handle week spanning two months
        if (startMonth === endMonth) {
          return `${weekStart.getDate()} - ${weekEnd.getDate()} de ${startMonth} ${endYear}`;
        } else {
          return `${weekStart.getDate()} de ${startMonth} - ${weekEnd.getDate()} de ${endMonth} ${endYear}`;
        }
      case "month":
        return `${month} ${year}`;
      default:
        return `${month} ${year}`;
    }
  };

  const handleMonthViewDateClick = (date: Date) => {
    onDateSelect(date);
    // If we're in month view and click a date, switch to day view
    if (currentView === "month") {
      onViewChange("day");
    }
  };

  const renderMonthNavigation = () => {
    if (currentView !== "month") return null;

    return (
      <div
        className="calendar-controls"
        style={{ marginBottom: "var(--space-md)" }}
      >
        <button onClick={handlePrevMonth}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <h3
          style={{
            margin: 0,
            fontSize: "1.25rem",
            color: "var(--color-accent)",
          }}
        >
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button onClick={handleNextMonth}>
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    );
  };

  // helpers (place near top of component)
  const safeToDateString = (d?: Date | null) =>
    d && d instanceof Date && !isNaN(d.getTime()) ? d.toDateString() : "";

  const parseSlotToMinutes = (slot: string) => {
    // expects e.g. "04:00 PM" or "4:00 PM"
    const [timePart, meridiem] = slot.split(" ");
    const [hStr, mStr] = timePart.split(":");
    let hour = parseInt(hStr, 10);
    const minutes = parseInt(mStr, 10);
    if (meridiem && meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (meridiem && meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;
    return hour * 60 + minutes;
  };

  // safer day filter: ensures apt.date exists and is a Date
  const getAppointmentsForDay = (date: Date) =>
    appointments.filter((apt) => {
      if (!apt || !apt.date) return false;
      if (!(apt.date instanceof Date)) return false;
      // Solo comparar año, mes y día, ignorando la hora
      return apt.date.toDateString() === date.toDateString();
    });

  // =================================================================================
  // FUNCIÓN NUEVA: Generar Slots de Tiempo Dinámicamente
  // =================================================================================
  const generateTimeSlots = (date: Date): string[] => {
    const dayAppointments = getAppointmentsForDay(date);

    // Si no hay citas, usamos un rango por defecto (ej: 9 AM a 5 PM)
    if (dayAppointments.length === 0) {
      const defaultSlots = [];
      for (let h = 9; h <= 17; h++) {
        // Horas 9 (AM) a 17 (5 PM)
        const hour = h % 12 === 0 ? 12 : h % 12;
        const meridiem = h < 12 || h === 24 ? "AM" : "PM";
        defaultSlots.push(`${hour.toString().padStart(2, "0")}:00 ${meridiem}`);
      }
      return defaultSlots;
    }

    // 1. Encontrar la hora de inicio y fin mínima/máxima de las citas
    let minHour = 24; // Máximo posible
    let maxHour = 0; // Mínimo posible

    dayAppointments.forEach((apt) => {
      if (apt.date && apt.date instanceof Date) {
        const hour = apt.date.getHours();
        if (hour < minHour) minHour = hour;
        // Asumimos que todas las citas duran al menos 1 hora para el slot
        if (hour > maxHour) maxHour = hour;
      }
    });

    // 2. Establecer el rango con un "colchón" de una hora
    const startHour = Math.max(0, minHour - 1); // 1 hora antes de la primera cita
    const endHour = Math.min(23, maxHour + 2); // 1 hora después de la última cita

    // 3. Generar los slots
    const slots: string[] = [];
    for (let h = startHour; h < endHour; h++) {
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const meridiem = h < 12 || h === 24 ? "AM" : "PM";
      slots.push(`${hour12.toString().padStart(2, "0")}:00 ${meridiem}`);
    }

    return slots;
  };
  // =================================================================================

  const renderDayWeekView = () => {
    // Renders the standard day/week grid (which you want to replace for 'week')

    // If currentView is 'week', we will render the new card view instead.
    if (currentView === "week") {
      return renderWeekCardView();
    }

    // Day view (remains slot-based)
    const dayAppointments = getAppointmentsForDay(selectedDate);
    // **USO DE LOS SLOTS DINÁMICOS**
    const dynamicTimeSlots = generateTimeSlots(selectedDate);

    return (
      <div id="day-view" className="calendar-day-slots">
        {dynamicTimeSlots.map((time) => {
          // ¡Ahora usa dynamicTimeSlots!
          // convert slot to minutes from midnight
          const slotStartMin = parseSlotToMinutes(time);
          const slotEndMin = slotStartMin + 60; // one-hour slot

          // find an appointment whose start minutes falls within the slot hour
          const appointment = dayAppointments.find((apt) => {
            if (!apt.date || !(apt.date instanceof Date)) return false;
            const apptMinutes =
              apt.date.getHours() * 60 + apt.date.getMinutes();
            return apptMinutes >= slotStartMin && apptMinutes < slotEndMin;
          });

          return (
            <div key={time} className="time-slot">
              <div className="time-slot-time">{time}</div>
              <div className="time-slot-appointment">
                {appointment ? (
                  <div
                    className="appointment-card"
                    style={{
                      borderLeftColor:
                        appointment.type === "virtual"
                          ? "var(--success)"
                          : appointment.status === "cancelled"
                            ? "var(--error)"
                            : "var(--color-accent)",
                      background:
                        appointment.type === "virtual"
                          ? "#E8F5E8"
                          : appointment.status === "cancelled"
                            ? "#FDE8E9"
                            : "var(--color-primary)",
                    }}
                  >
                    <strong>{appointment.patientName}</strong>
                    <small>
                      {appointment.service} - {appointment.duration}
                    </small>
                    {appointment.type === "virtual" && (
                      <small> (Virtual)</small>
                    )}
                  </div>
                ) : (
                  <div className="empty-slot">
                    <small
                      style={{
                        color: "var(--neutral-300)",
                        fontStyle: "italic",
                      }}
                    >
                      Sin citas programadas
                    </small>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // =================================================================================
  // 2. New Week Card View Renderer (No changes needed here)
  // =================================================================================
  const renderWeekCardView = () => {
    const weekDates = getDatesOfWeek(selectedDate);

    return (
      <div id="week-card-view" className="card-view-wrapper">
        {weekDates.map((date, index) => {
          const dayAppointments = getAppointmentsForDay(date);
          const isToday = date.toDateString() === new Date().toDateString();
          const dayName = dayNames[index];
          const dateString = `${date.getDate()} de ${
            monthNames[date.getMonth()]
          }`;

          // Use the "Elevated Card View" style structure from your previous HTML
          const cardClasses = isToday
            ? "day-card elevated-card today"
            : "day-card elevated-card";

          return (
            <div key={date.toDateString()} className={cardClasses}>
              <h4>{dayName}</h4>
              <span className="date-text">{dateString}</span>
              <ul>
                {dayAppointments.length > 0 ? (
                  dayAppointments.map((apt) => (
                    <li key={apt.id} onClick={() => onDateSelect(date)}>
                      <span className="event-time">
                        {apt.time || apt.duration}
                      </span>
                      {apt.patientName}
                    </li>
                  ))
                ) : (
                  <li>
                    <span className="event-time">...</span>
                    Sin citas
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  // =================================================================================
  // 3. Month View (Unchanged for this request)
  // =================================================================================
  const renderMonthView = () => {
    // ... (Your existing renderMonthView logic remains here) ...
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayIndex = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const daysInMonth = lastDay.getDate();

    const prevMonth = new Date(year, month - 1, 1);
    const daysInPrevMonth = new Date(
      prevMonth.getFullYear(),
      prevMonth.getMonth() + 1,
      0,
    ).getDate();

    const days = [];

    // Previous month days
    for (
      let i = daysInPrevMonth - firstDayIndex + 1;
      i <= daysInPrevMonth;
      i++
    ) {
      const date = new Date(prevMonth);
      date.setDate(i);
      days.push(
        <div
          key={`prev-${i}`}
          className="day-cell other-month"
          onClick={() => handleMonthViewDateClick(date)}
        >
          <span>{i}</span>
        </div>,
      );
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dayAppointments = getAppointmentsForDay(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();

      const dayClasses = ["day-cell"];
      if (isToday) dayClasses.push("today");
      if (isSelected) dayClasses.push("selected");
      if (date.getMonth() !== month) dayClasses.push("other-month");

      days.push(
        <div
          key={`current-${i}`}
          className={dayClasses.join(" ")}
          onClick={() => handleMonthViewDateClick(date)}
        >
          <span>{i}</span>
          {dayAppointments.slice(0, 3).map((apt, index) => (
            <div
              key={apt.id}
              className={`appointment-mini-card ${
                apt.type === "virtual"
                  ? "success"
                  : apt.status === "cancelled"
                    ? "error"
                    : "accent"
              }`}
            >
              {index === 2 && dayAppointments.length > 3
                ? `${dayAppointments.length - 2} Citas...`
                : apt.patientName.split(" ")[0]}
            </div>
          ))}
          {dayAppointments.length === 0 && (
            <div className="empty-day-indicator">
              <small style={{ color: "var(--neutral-300)" }}>Sin citas</small>
            </div>
          )}
        </div>,
      );
    }

    // Next month days
    const totalCells = 42; // 6 weeks
    const nextMonthDays = totalCells - days.length;
    const nextMonth = new Date(year, month + 1, 1);

    for (let i = 1; i <= nextMonthDays; i++) {
      const date = new Date(nextMonth);
      date.setDate(i);
      days.push(
        <div
          key={`next-${i}`}
          className="day-cell other-month"
          onClick={() => handleMonthViewDateClick(date)}
        >
          <span>{i}</span>
        </div>,
      );
    }

    return (
      <div id="month-view" className="calendar-month-view">
        {renderMonthNavigation()}
        <div className="month-grid-full">
          {dayNames.map((day) => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  // 4. Main Render Logic
  const getButtonClass = (view: ViewType) => {
    const baseClass = "btn btn--small view-toggle";
    return currentView === view
      ? `${baseClass} active-toggle btn--primary`
      : `${baseClass} btn--secondary`;
  };

  return (
    <div className="main-calendar-view">
      <div className="main-calendar-header">
        <h2 id="view-title">{getViewTitle()}</h2>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className={getButtonClass("day")}
            onClick={() => onViewChange("day")}
          >
            Día
          </button>
          <button
            className={getButtonClass("week")}
            onClick={() => onViewChange("week")}
          >
            Semana
          </button>
          <button
            className={getButtonClass("month")}
            onClick={() => onViewChange("month")}
          >
            Mes
          </button>
        </div>
      </div>

      {/* CONDITIONAL RENDERING UPDATE */}
      {
        currentView === "month"
          ? renderMonthView()
          : currentView === "week"
            ? renderWeekCardView() // Use the new card view for 'week'
            : renderDayWeekView() // Keep the slot-based view for 'day'
      }
    </div>
  );
};

export default CalendarView;
